import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [state, setState] = useState("checking");
  const [plan, setPlan] = useState("");
  const tries = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      tries.current += 1;
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`, { credentials: "include" });
        const data = await res.json();
        if (data.payment_status === "paid") { setPlan(data.plan || ""); setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch {}
      if (tries.current >= 10) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [params]);

  return (
    <div className="min-h-screen bg-[#060b1a] text-white flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="text-center max-w-md">
        {state === "checking" && (<><Loader2 className="h-12 w-12 mx-auto animate-spin text-[#7ea6ff]" /><h1 className="font-display text-2xl font-extrabold mt-5">Confirming your payment…</h1><p className="text-slate-400 mt-2 text-sm">One moment — we're checking with Stripe.</p></>)}
        {state === "paid" && (<>
          <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-400" />
          <h1 className="font-display text-3xl font-extrabold mt-5" data-testid="payment-success-title">You're on the {plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : "new"} plan!</h1>
          <p className="text-slate-400 mt-3">Your plan is active — new portfolios are unlocked. Welcome aboard.</p>
          <Button className="mt-7 h-11 px-7" onClick={() => nav("/dashboard")} data-testid="payment-success-dashboard">Go to my dashboard</Button>
        </>)}
        {(state === "error" || state === "timeout") && (<>
          <XCircle className="h-14 w-14 mx-auto text-amber-400" />
          <h1 className="font-display text-2xl font-extrabold mt-5">{state === "timeout" ? "Still processing" : "Payment not completed"}</h1>
          <p className="text-slate-400 mt-3 text-sm">{state === "timeout" ? "Your payment is taking a little longer — check your email for a Stripe receipt, or refresh this page shortly." : "No charge went through. You can try again from the pricing page."}</p>
          <Button variant="outline" className="mt-6 h-11 px-6 border-white/30 bg-white/5 text-white hover:bg-white/15 hover:text-white" onClick={() => nav("/pricing")} data-testid="payment-error-pricing">Back to pricing</Button>
        </>)}
      </div>
    </div>
  );
}
